import {
  Box,
  captureException,
  Container,
  Flex,
  Heading,
  NextImage,
  Progress,
  Text,
  Tooltip,
  useBreakpointValue,
  useSearchNft,
} from '@dapperlabs/core-fe';
import { getCookie } from 'cookies-next';
import { GetServerSidePropsContext, NextPage } from 'next';
import Link from 'next/link';
import { useSession } from 'src/edge/session';
import { Countdown } from 'src/general/components/Countdown';
import Head from 'src/general/components/Head/Head';
import { MOMENTS_QUERY_LIMIT } from 'src/general/constants/config';
import { useTranslation } from 'src/lib/translations';
import { getStaticCollectionContent } from 'src/modules/CollectionsPageContent/staticContent';
import { getEditionsFromCollections } from 'src/modules/CollectionsPageContent/utils';
import {
  searchGolazosNftByOwnerAddress,
  SEARCH_GOLAZOS_NFT,
} from 'src/modules/Queries/searchNft';
import { IconRightArrow } from 'src/theme/Icon';

interface Props {
  collection?: any;
  collectionEntryId?: string;
}

const CollectionsPage = ({ collection, collectionEntryId }: Props) => {
  const { t } = useTranslation();
  const { title, description, heroImage, levels, endDate } = collection;
  const isMdUp = useBreakpointValue({ md: true });
  const { state: sessionState } = useSession();

  const flowAddress = sessionState?.context?.profile?.flowAddress;

  const { moments } = useSearchNft({
    aggregationResponseType: 'searchGolazosNft',
    options: {
      skip: !flowAddress,
    },
    query: SEARCH_GOLAZOS_NFT(),
    variables: searchGolazosNftByOwnerAddress(
      flowAddress,
      {},
      MOMENTS_QUERY_LIMIT,
    ),
  });

  return (
    <>
      <Head
        description={t('Head.CollectionGroupPage.Description', {
          collectionDescription: description,
        })}
        subtitle=''
        title={t('Head.CollectionGroupPage.Title', {
          collectionTitle: title,
        })}
      />
      <Container
        as='main'
        maxW={{ base: '100%', md: '90rem', sm: '100%' }}
        mt={{ base: 2, md: 4 }}
        p={0}
        pb={6}
      >
        <Flex
          as='section'
          pos='relative'
          alignItems='center'
          flexDirection={{ base: 'column', lg: 'row' }}
          gap={{ base: 0, lg: 20 }}
          mb='3.5rem'
        >
          <Box flex={1}>
            <NextImage
              width='720px'
              height='450px'
              alt={heroImage?.fields.title}
              layout='intrinsic'
              src={heroImage?.fields.file.url}
            />
          </Box>
          <Box
            flex={1}
            mx={{ base: 4, lg: 0 }}
            p={{ base: 0, md: 6 }}
            border={['none', 'none', '1px solid #393B45']}
            borderRadius='20px'
          >
            <Heading
              as='h1'
              color='white'
              fontFamily='poster'
              fontSize={['5xl', '6xl']}
              fontWeight='500'
            >
              {title}
            </Heading>
            <Text
              mb={2}
              color='gray.100'
              fontSize={['md', 'lg']}
              lineHeight='1.4'
            >
              {description}
            </Text>
            {endDate && (
              <Countdown
                date={endDate}
                numberProps={{
                  fontFamily: 'mono',
                  fontSize: isMdUp ? '4xl' : '3xl',
                  fontWeight: 400,
                }}
                textSize={isMdUp ? 'lg' : 'md'}
              />
            )}
          </Box>
        </Flex>
        <Box as='section' px={isMdUp && 16}>
          {levels.map(({ fields }) => {
            const {
              title,
              description: levelDescription,
              image,
              rewards,
              levelEntryId,
              collections,
            } = fields;
            const levelCollections = collections.map(
              (collection) => collection.fields,
            );

            const editions = getEditionsFromCollections(levelCollections);
            const totalEditions = editions?.length || 0;

            const ownedEditions = editions?.reduce((acc, id) => {
              return moments.find(
                (moment: any) => String(moment.edition.id) === String(id),
              )
                ? acc + 1
                : acc;
            }, 0);

            const hasRewards = rewards && rewards.length > 0;

            return (
              <Link
                key={levelEntryId}
                href={`/collections/${collectionEntryId}/level/${levelEntryId}`}
              >
                <Box
                  mb={4}
                  mx={{ base: 4, lg: 0 }}
                  border='solid 1px #393B45'
                  borderRadius='20px'
                  cursor='pointer'
                >
                  <Flex
                    pos='relative'
                    alignItems='center'
                    direction={isMdUp ? 'row' : 'column'}
                  >
                    <Flex
                      alignItems='center'
                      flex='2'
                      pl={isMdUp && 4}
                      py={isMdUp && 4}
                    >
                      {isMdUp ? (
                        <Box
                          pos='absolute'
                          top={0}
                          right='60%'
                          bottom={0}
                          left={0}
                          bgImage={image.fields.file.url}
                          bgSize='cover'
                          bgRepeat='no-repeat'
                          borderLeftRadius='20px'
                          bgPos='center'
                        />
                      ) : (
                        <NextImage
                          width='720px'
                          height='450px'
                          alt={image.fields.title}
                          src={image.fields.file.url}
                          style={{
                            borderTopLeftRadius: isMdUp ? 0 : '20px',
                            borderTopRightRadius: isMdUp ? 0 : '20px',
                          }}
                        />
                      )}
                    </Flex>
                    <Flex
                      pos='relative'
                      direction='column'
                      flex='3'
                      pr={8}
                      pl={isMdUp ? 32 : 8}
                      py={8}
                    >
                      {!isMdUp && hasRewards && (
                        <Box
                          sx={{ backdropFilter: 'blur(15px)' }}
                          pos='absolute'
                          top={-5}
                          right={5}
                          alignItems='center'
                          justifyContent='center'
                          display='flex'
                          width={45}
                          height={45}
                          borderRadius='50%'
                          bgColor='rgba(138, 148, 166, 0.25)'
                        >
                          <IconRightArrow />
                        </Box>
                      )}
                      <Heading
                        as='h2'
                        mb={2}
                        fontFamily='poster'
                        fontSize={['4xl', '5xl']}
                        fontWeight='500'
                        textTransform='uppercase'
                      >
                        {title}
                      </Heading>
                      <Text
                        mb={6}
                        color='gray.100'
                        fontSize={['md', 'lg']}
                        lineHeight='1.4'
                      >
                        {levelDescription}
                      </Text>
                      <Box mb={6}>
                        <Text mb={4} color='gray' fontFamily='mono'>
                          {t('CollectionGroup.Level.Progress')}
                        </Text>
                        <Flex alignItems='center'>
                          <Progress
                            sx={{
                              'div[role=progressbar]': {
                                backgroundColor: '#62D43F',
                              },
                              flex: '1',
                            }}
                            value={Math.trunc(
                              (ownedEditions / totalEditions) * 100,
                            )}
                          />
                          <Text pl={6} fontFamily='mono' fontSize='sm'>
                            {ownedEditions}/{totalEditions}
                          </Text>
                        </Flex>
                      </Box>
                      {hasRewards && (
                        <Box>
                          <Text mb={4} color='gray' fontFamily='mono'>
                            {t('CollectionGroup.Level.Reward')}
                          </Text>
                          <Flex
                            pos='relative'
                            alignItems='center'
                            justifyContent='space-between'
                          >
                            <Flex pos='relative' alignItems='center'>
                              {rewards?.map((reward, index) => (
                                <Box
                                  key={reward.title}
                                  p={2}
                                  borderTop='solid 1px #2B2C33'
                                  borderRight={
                                    index === rewards.length - 1
                                      ? 'solid 1px #2B2C33'
                                      : 'none'
                                  }
                                  borderBottom='solid 1px #2B2C33'
                                  borderLeft='solid 1px #2B2C33'
                                  borderRightRadius={
                                    index === rewards.length - 1 && '12px'
                                  }
                                  borderLeftRadius={index === 0 && '12px'}
                                >
                                  <Tooltip
                                    key={reward.title}
                                    sx={{ display: 'flex' }}
                                    label={reward.fields.title}
                                    placement='top'
                                  >
                                    <Box>
                                      <NextImage
                                        width='122px'
                                        height='122px'
                                        alt={
                                          reward.fields.rewardImage.fields.title
                                        }
                                        layout='intrinsic'
                                        src={
                                          reward.fields.rewardImage.fields.file
                                            .url
                                        }
                                      />
                                    </Box>
                                  </Tooltip>
                                </Box>
                              ))}
                            </Flex>
                            {isMdUp && (
                              <Box
                                sx={{ backdropFilter: 'blur(15px)' }}
                                pos='absolute'
                                right={0}
                                alignItems='center'
                                justifyContent='center'
                                display='flex'
                                width={45}
                                height={45}
                                borderRadius='50%'
                                bgColor='rgba(138, 148, 166, 0.25)'
                              >
                                <IconRightArrow />
                              </Box>
                            )}
                          </Flex>
                        </Box>
                      )}
                    </Flex>
                  </Flex>
                </Box>
              </Link>
            );
          })}
        </Box>
      </Container>
    </>
  );
};

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const {
    params: { id: collectionEntryId },
    req,
    res,
  } = context;
  try {
    const locale = (getCookie('language', { req, res }) as string) || 'en-US';
    const collectionId = Array.isArray(collectionEntryId)
      ? collectionEntryId[0]
      : collectionEntryId;

    const staticCollection = getStaticCollectionContent(collectionId, locale);

    if (!staticCollection) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        collection: staticCollection,
        collectionEntryId: collectionId,
      },
    };
  } catch (error) {
    await captureException(error, { req, res });
    return {
      props: {
        collection: null,
        collectionEntryId: '',
      },
    };
  }
}

const CollectionsPagePresenter: NextPage = ({
  error,
  ...props
}: {
  error?: boolean;
}) => {
  if (error) return <>Unable to load collection.</>;
  return <CollectionsPage {...props} />;
};

export default CollectionsPagePresenter;
